import _ from 'underscore';
import { DeterministicView, helpers } from '@bingads-webui/deterministic-view';
import { PrimaryDateRangePanel } from '../panels/primary/primary-date-range-panel';
import { ComparedDateRangePanel } from '../panels/compared/compared-date-range-panel';

import template from './date-filter-panels-container-template.pug';

const { updateView } = helpers;

/**
 * Container class for the panels to show in the date filter
 * @class DateFilterPanelsContainer
 * @extends {View}
 * @param {Date} today - The date for today to be used in calculating date ranges
 * @param {Object} i18n - The date for today to be used in calculating date ranges
 * @param {string} dateFormat - The date for today to be used in calculating date ranges
 * @param {Boolean} isComparisonEnabled - Whether compared date range panel is enabled or not
 * @param {Function} [getDateRanges] - The optional function to get primary date ranges
 * @param {Function} [enabledRangesPredicate] - The optional function to filter primary date ranges
 * It is passed each date range in turn and should return true for the ranges to use
 * @param {Function} [getComparedDateRanges] - The optional function to get compared date ranges

 */
export class DateFilterPanelsContainer extends DeterministicView {
  initialize(options) {
    this.options = options;
    const {
      i18n,
      dateFormat,
      today,
      getDateRanges,
      getComparedDateRanges,
      enabledRangesPredicate,
      isComparisonEnabled,
      minDate,
      maxDate,
      defaultTimeRange = {},
    } = this.options;

    _.defaults(defaultTimeRange, {
      start: new Date('2017/01/01 8:00'),
      end: new Date('2017/01/01 17:00'),
    });
    this.setChild('primary-date-range-panel', new PrimaryDateRangePanel({
      dateRangesAccessor: getDateRanges,
      enabledRangesPredicate,
      i18n,
      dateFormat,
      today,
      showDates: true,
      minDate,
      maxDate,
      defaultTimeRange,
    }));

    const primaryDateRangePanel = this.getChild('primary-date-range-panel');
    primaryDateRangePanel.on('range-checked', (checked) => {
      this.rangeChecked(checked);
    });

    if (isComparisonEnabled) {
      this.setChild(
        'compared-date-range-panel',
        new ComparedDateRangePanel({
          dateRangesAccessor: getComparedDateRanges,
          i18n,
          dateFormat,
          today,
          showDates: false,
        })
      );
      const comparedDateRangePanel = this.getChild('compared-date-range-panel');

      comparedDateRangePanel.on('range-checked', checked => this.rangeChecked(checked));
      primaryDateRangePanel.on('range-checked', checked => comparedDateRangePanel.onPrimaryRangeChanged(checked));
    }
  }

  /**
 * Events fired by the date filter panels container
 */
  get events() {
    return {
      'click .apply': () => {
        this.apply();
      },
      'click .toggle-button': (e) => {
        this.model.set({
          comparisonToggleChecked: !this.model.get('comparisonToggleChecked'),
        });
        e.stopPropagation();
      },
    };
  }

  /**
   * Sets the state of the panels container
   * @param {Object} newState.checkedSelection - Date filter preference to use by default
   * @param {Object} newState.checkedSelection.primaryRange - Primary range selected
   * @param {Date} newState.checkedSelection.primaryRange.start - Primary range start date
   * @param {Date} newState.checkedSelection.primaryRange.end - Primary range end date
   * @param {Number} [newState.checkedSelection.primaryRange.grain] - Grain for the primary range
   * @param {string} newState.checkedSelection.primaryRange.key - Key for the primary range
   * @param {Object} [newState.checkedSelection.comparedRange] - Compared date range selected
   * @param {Date} newState.checkedSelection.comparedRange.start - Compared range start date
   * @param {Date} newState.checkedSelection.comparedRange.end - Compared range end date
   * @param {string} newState.checkedSelection.comparedRange.key - Key for the compared range
   * @returns {Object} this
   */
  setState({
    checkedSelection,
    comparisonToggleChecked,
  }) {
    this.model.set({ comparisonToggleChecked });

    this.getChild('primary-date-range-panel')
      .setState({ selection: checkedSelection.primaryRange });

    if (this.options.isComparisonEnabled && comparisonToggleChecked) {
      this.getChild('compared-date-range-panel')
        .setState({
          selection: checkedSelection.comparedRange || {},
          primaryRange: checkedSelection.primaryRange,
        });
    }

    const selectedRanges = this.getCheckedRanges();

    this.model.set({
      checkedSelection: selectedRanges,
    });
    return this;
  }

  /**
 * Callback function for when a date range has been checked
 * @returns {undefined}
 */
  rangeChecked() {
    const checkedSelection = this.getCheckedRanges();
    updateView(this, { checkedSelection });

    const customDateRangeChecked = _.some(checkedSelection, range => range.isCustom);
    if (!customDateRangeChecked && !this.getState().comparisonToggleChecked) {
      this.apply();
    }
  }

  /**
 * Gets the state of the date filter panels
 * @returns {state} - The selected date ranges
 */
  getCheckedRanges() {
    const state = {
      primaryRange: this.getChild('primary-date-range-panel').getCheckedRange(),
    };
    if (this.getState().comparisonToggleChecked) {
      _.extend(state, {
        comparedRange: this.getChild('compared-date-range-panel').getCheckedRange(),
      });
    }
    return state;
  }

  /**
 * Apply the currently selected date ranges, and trigger change event if they're valid
 * @returns {undefined}
 */
  apply() {
    let checkedSelection = this.getCheckedRanges();
    if (checkedSelection.primaryRange.invalid) {
      return;
    }
    if (checkedSelection.comparedRange) {
      this.getChild('compared-date-range-panel').onPrimaryRangeChanged(checkedSelection.primaryRange);
    }

    checkedSelection = this.getCheckedRanges();

    if (checkedSelection.comparedRange && checkedSelection.comparedRange.invalid) {
      return;
    }

    updateView(this, { checkedSelection });
    const appliedSelection = this.getState().comparisonToggleChecked ?
      checkedSelection : _.pick(checkedSelection, 'primaryRange');
    this.trigger('date-range-selected', appliedSelection);
  }

  get template() {
    return template;
  }
}
